import { Link } from "@tanstack/react-router";
import { ArrowRight, Play, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { JSX } from "react";
import { FadeUp, MotionButton } from "@/lib/animations/components";
import {
  staggerOrchestrator,
  lineReveal,
  scaleXRevealProps,
  listItem,
  arrowNudge,
} from "@/lib/animations/variants";

const trustPoints = [
  "KRA Authorized Agents",
  "ICPAK Certified Professionals",
  "Confidential & Secure",
];

export function HeroSection(): JSX.Element {
  return (
    <section className="bg-[#FAF7F2]">
      <div className="lg:hidden">
        <FadeUp>
          <img
            src="/images/Rosewood 1.png"
            alt="Rosewood Tax Consultancy"
            className="w-full h-auto block"
          />
        </FadeUp>
        <div className="px-6 py-12">
          <HeroContent />
        </div>
      </div>

      <div className="hidden lg:flex min-h-screen">
        <div className="flex-1 flex items-center px-10 xl:px-16 py-24">
          <div className="w-full max-w-lg">
            <HeroContent />
          </div>
        </div>

        <motion.div
          className="w-[46%] xl:w-[48%] flex-shrink-0 relative"
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
        >
          <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
            <img
              src="/images/Rosewood 1.png"
              alt="Rosewood Tax Consultancy professionals"
              className="w-full h-full object-contain"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function HeroContent() {
  return (
    <>

      <motion.h1
        variants={staggerOrchestrator(0.13, 0.15)}
        initial="initial"
        animate="animate"
        className="text-5xl xl:text-[60px] font-serif font-semibold text-[#1A1A1A] leading-[1.1] tracking-tight mb-6"
      >
        <motion.span variants={lineReveal} className="block">
          Tax Expertise
        </motion.span>
        <motion.span variants={lineReveal} className="block text-[#6B1E2A]">
          You Can Trust.
        </motion.span>
        <motion.span variants={lineReveal} className="block">
          <span className="text-[#C9A84C]">Results</span> You Feel.
        </motion.span>
      </motion.h1>

      <motion.div
        {...scaleXRevealProps(0.54)}
        className="origin-left w-12 h-[2px] bg-[#C9A84C] mb-6"
      />

      <FadeUp
        delay={0.62}
        className="text-[17px] text-[#6B6B6B] leading-relaxed mb-8"
      >
        From KRA registration and eTIMS compliance to audit defence and
        international tax advisory — Rosewood handles every dimension of your
        tax life.
      </FadeUp>

      <motion.div
        variants={staggerOrchestrator(0.08, 0.72)}
        initial="initial"
        animate="animate"
        className="flex flex-wrap gap-x-6 gap-y-2 mb-10"
      >
        {trustPoints.map((point) => (
          <motion.div
            key={point}
            variants={listItem}
            className="flex items-center gap-1.5 text-sm text-[#6B6B6B]"
          >
            <CheckCircle size={13} className="text-[#6B1E2A] flex-shrink-0" />
            {point}
          </motion.div>
        ))}
      </motion.div>

      <FadeUp delay={0.84} className="flex flex-wrap items-center gap-4">
        <CTAButtons />
      </FadeUp>
    </>
  );
}

function CTAButtons() {
  return (
    <>
      <MotionButton lift>
        <Link
          to="/contact"
          className="group inline-flex items-center gap-2 px-7 py-3.5 bg-[#6B1E2A] text-white font-semibold rounded-xl hover:bg-[#4A1219] transition-colors"
        >
          Free Consultation
          <motion.span
            variants={arrowNudge}
            initial="rest"
            whileHover="hovered"
            className="inline-flex"
          >
            <ArrowRight size={16} />
          </motion.span>
        </Link>
      </MotionButton>

      <motion.button
        className="group inline-flex items-center gap-3 px-4 py-3.5 text-[#1A1A1A] font-medium hover:text-[#6B1E2A] transition-colors"
        whileHover="hovered"
        initial="rest"
        animate="rest"
      >
        <motion.span
          className="w-10 h-10 bg-white border border-[#E8E0D0] rounded-full flex items-center justify-center shadow-sm"
          variants={{
            rest: { boxShadow: "0 0 0 0px rgba(201,168,76,0)" },
            hovered: {
              boxShadow: [
                "0 0 0 0px rgba(201,168,76,0.35)",
                "0 0 0 10px rgba(201,168,76,0)",
              ],
              transition: { duration: 0.55, ease: "easeOut" },
            },
          }}
        >
          <Play size={11} className="text-[#6B1E2A] ml-0.5" fill="#6B1E2A" />
        </motion.span>
        Watch Overview
      </motion.button>
    </>
  );
}
